import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, X, Phone, Trash, Clock, Chat, EnvelopeSimple, Globe } from "@phosphor-icons/react";
import { LeadDateFilter, DownloadBtn } from "../../components/crm/LeadDateFilter";
import { rangeFor, inRange, exportLeadsCsv, exportLeadsPdf, delayHrs, delayLevel, fmtDelay, fmtDate } from "./leadExport";

const STATUSES = ["new", "contacted", "interested", "converted", "lost"];
const STATUS_COLOR = {
  new: "#D4E0D1", contacted: "#F9E4B7", interested: "#C25D38",
  converted: "#2D5A27", lost: "#8A8780",
};

// Iter 138 — Friendly source labels + inbound-vs-manual classification.
// Anything coming from our own surfaces (chat widget, contact form, /me,
// self-registration) shows a small icon so staff spot warm leads.
const SOURCE_META = {
  chat_widget:  { label: "Chat widget",   Icon: Chat,           bg: "bg-[#EEF2FA]", fg: "text-[#0B2B66]" },
  chat:         { label: "Chat widget",   Icon: Chat,           bg: "bg-[#EEF2FA]", fg: "text-[#0B2B66]" },
  homepage:     { label: "Contact form",  Icon: EnvelopeSimple, bg: "bg-[#EEF7EC]", fg: "text-[#2D5A27]" },
  contact_form: { label: "Contact form",  Icon: EnvelopeSimple, bg: "bg-[#EEF7EC]", fg: "text-[#2D5A27]" },
  website:      { label: "Website",       Icon: Globe,          bg: "bg-[#EEF7EC]", fg: "text-[#2D5A27]" },
  self_register:{ label: "Customer signup", Icon: Globe,        bg: "bg-[#EEF7EC]", fg: "text-[#2D5A27]" },
  whatsapp:     { label: "WhatsApp",      Icon: Chat,           bg: "bg-[#E4F5E4]", fg: "text-[#1F5E1F]" },
  referral:     { label: "Referral",      Icon: null,           bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]" },
  "walk-in":    { label: "Walk-in",       Icon: null,           bg: "bg-[#EEEBE2]", fg: "text-[#5C5A55]" },
  social:       { label: "Social",        Icon: null,           bg: "bg-[#F4E4F4]", fg: "text-[#6B2E6B]" },
  event:        { label: "Event",         Icon: null,           bg: "bg-[#FDE9E4]", fg: "text-[#7A2E18]" },
};
const sourceMeta = (src) => SOURCE_META[src] || {
  label: src || "manual", Icon: null, bg: "bg-[#EEEBE2]", fg: "text-[#5C5A55]",
};

// Return {hrs, level} — level: 'ok' | 'warn' | 'stale'
const stalenessOf = (l, now) => {
  const anchor = l.updated_at || l.created_at;
  if (!anchor) return { hrs: 0, level: "ok" };
  const hrs = (now - new Date(anchor).getTime()) / 3600000;
  if (hrs >= 34) return { hrs, level: "stale" };
  if (hrs >= 24) return { hrs, level: "warn" };
  return { hrs, level: "ok" };
};

const relativeAgo = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function Leads() {
  const { can } = useAuth();
  const canExport = can(["superadmin", "admin"]);
  const [preset, setPreset] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [stage, setStage] = useState("");
  const canEdit = can(["superadmin", "admin", "manager", "service_coordinator", "office_staff", "office"]);
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", source: "website",
    location: "", notes: "", status: "new",
  });
  // Ticker so blink states re-evaluate as time passes (30s cadence is
  // plenty — we're crossing 24h / 34h thresholds).
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    const [l, p] = await Promise.all([api.get("/leads"), api.get("/leads/pipeline")]);
    setLeads(l.data); setPipeline(p.data);
  };
  useEffect(() => { load(); }, []);
  // Iter 138 — auto-refresh so newly submitted contact-form / chat-widget
  // leads appear without a manual reload.
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      toast.success("Lead created");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", source: "website", location: "", notes: "", status: "new" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (id, status) => {
    try { await api.post(`/leads/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    try { await api.delete(`/leads/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const range = rangeFor(preset, custom);
  const dated = useMemo(() => leads.filter(l => inRange(l.created_at, range)),
    [leads, range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = stage ? dated.filter(l => l.status === stage) : dated;
  // Group leads by status once per render
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map(s => [s, []]));
    dated.forEach(l => { if (g[l.status]) g[l.status].push(l); });
    return g;
  }, [dated]);

  return (
    <AppShell title="Leads Pipeline" subtitle="Module 4 · CRM"
      actions={canEdit &&
        <button data-testid="btn-new-lead" onClick={() => setOpen(true)}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>New lead
        </button>
      }
    >
      <div className="mb-4 bg-white border border-[#E5E2D9] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="leads-toolbar">
        <LeadDateFilter preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} testPrefix="leads" />
        <div className="flex items-center gap-1 flex-wrap" data-testid="leads-stage-tabs">
          {[["", "All", dated.length], ...STATUSES.map(s => [s, s, grouped[s]?.length || 0])].map(([k, l, n]) => (
            <button key={k || "all"} onClick={() => setStage(k)} data-testid={`leads-stage-${k || "all"}`}
                    className={`h-8 px-3 rounded-full text-xs capitalize border transition-colors ${stage === k ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
              {l} <span className="font-mono opacity-70">{n}</span>
            </button>
          ))}
        </div>
        {canExport && (
          <div className="ml-auto flex gap-2">
            <DownloadBtn label="Download CSV" testid="leads-download-csv" disabled={!visible.length}
                         onClick={() => exportLeadsCsv(visible, range, stage)} />
            <DownloadBtn label="Download PDF" testid="leads-download-pdf" disabled={!visible.length}
                         onClick={() => exportLeadsPdf(visible, range, stage, stage ? [stage] : STATUSES)} />
          </div>
        )}
      </div>
      <StaleLegend/>
      {stage ? <StageTable leads={visible} now={now} /> :
      <div className="grid grid-cols-5 gap-4 overflow-x-auto" data-testid="kanban">
        {STATUSES.map(s => {
          const items = grouped[s] || [];
          const total = preset === "all" ? (pipeline.find(p => p.status === s)?.count || 0) : items.length;
          return (
            <div key={s} className="bg-white border border-[#E5E2D9] rounded-md flex flex-col min-w-[240px]">
              <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: STATUS_COLOR[s] }}/>
                <span className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#1A1A1A]">{s}</span>
                <span className="ml-auto text-[10px] font-mono text-[#8A8780]">{total}</span>
              </div>
              <div className="p-2 space-y-2 flex-1 min-h-[200px]">
                {items.length === 0 && <div className="text-xs text-[#8A8780] text-center py-6">—</div>}
                {items.map(l => (
                  <LeadCard key={l.id} lead={l} status={s} now={now}
                             canEdit={canEdit}
                             onMove={move} onDelete={del}/>
                ))}
              </div>
            </div>
          );
        })}
      </div>}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">New lead</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={create} className="space-y-3" data-testid="lead-form">
              {[
                ["name", "Name", true], ["phone", "Phone", true],
                ["email", "Email", false], ["location", "Location", false],
                ["notes", "Notes", false],
              ].map(([k, lbl, req]) => (
                <div key={k}>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">{lbl}</label>
                  <input data-testid={`lead-${k}`} required={req} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
              ))}
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Source</label>
                <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                  {["website", "referral", "walk-in", "social", "event"].map(x => <option key={x}>{x}</option>)}
                </select>
              </div>
              <button data-testid="lead-save" type="submit" className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Create</button>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const DELAY_CLS = { stale: "text-[#7A2E18] font-semibold", warn: "text-[#7A5A11] font-semibold", ok: "text-[#5C5A55]" };

function StageTable({ leads, now }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto" data-testid="leads-stage-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
          <tr>{["Lead", "Phone", "Source", "Status", "Captured", "Last updated", "Delay"].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8A8780]">No leads in this stage for the selected dates.</td></tr>}
          {leads.map((l, i) => {
            const h = delayHrs(l, now);
            return (
              <tr key={l.id} className={i % 2 ? "bg-[#FAFAF7]" : ""} data-testid={`leads-row-${l.id}`}>
                <td className="px-3 py-2 font-medium">{l.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.phone}</td>
                <td className="px-3 py-2 text-xs">{sourceMeta(l.source).label}</td>
                <td className="px-3 py-2 text-xs capitalize">{l.status}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(l.created_at)}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(l.updated_at || l.created_at)}</td>
                <td className={`px-3 py-2 text-xs ${DELAY_CLS[delayLevel(h)]}`} data-testid={`leads-delay-${l.id}`}>{fmtDelay(h)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StaleLegend() {
  return (
    <div className="mb-3 flex items-center gap-4 text-[11px] text-[#5C5A55]">
      <div className="uppercase tracking-[0.16em]">Stagnation:</div>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#F9E4B7] lead-blink-warn"/>
        24–34h
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#C25D38] lead-blink-stale"/>
        &gt; 34h in same status
      </span>
    </div>
  );
}

function LeadCard({ lead, status, canEdit, now, onMove, onDelete }) {
  const stale = stalenessOf(lead, now);
  const sm = sourceMeta(lead.source);
  const SIcon = sm.Icon;
  // Blink class controls the border animation defined in index.css.
  const blinkCls =
    stale.level === "stale" ? "lead-blink-stale ring-2 ring-[#C25D38]" :
    stale.level === "warn"  ? "lead-blink-warn ring-2 ring-[#F9E4B7]"  : "";
  return (
    <div
      data-testid={`lead-${lead.id}`}
      data-stale-level={stale.level}
      className={`border border-[#E5E2D9] rounded-md p-3 bg-white hover:bg-[#F7F6F2] transition-colors ${blinkCls}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm">{lead.name}</div>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.14em] whitespace-nowrap ${sm.bg} ${sm.fg}`}
              title={`Source: ${lead.source || "manual"}`}>
          {SIcon && <SIcon size={9} weight="bold"/>}
          {sm.label}
        </span>
      </div>
      <div className="text-xs text-[#5C5A55] flex items-center gap-1 mt-1"><Phone size={10}/>{lead.phone}</div>
      {lead.notes && (
        <div className="text-[11px] text-[#7A7770] mt-1 line-clamp-2" title={lead.notes}>
          {lead.notes}
        </div>
      )}

      {/* Timestamps — first contact + time in current status */}
      <div className="mt-2 pt-2 border-t border-dashed border-[#E5E2D9] space-y-0.5">
        <div className="text-[10px] text-[#8A8780] flex items-center gap-1">
          <Clock size={9}/>
          <span>Captured {relativeAgo(lead.created_at)}</span>
          <span className="opacity-60">·</span>
          <span title={new Date(lead.created_at).toLocaleString()} className="font-mono">
            {new Date(lead.created_at).toLocaleString([], {
              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </span>
        </div>
        <div className={`text-[10px] flex items-center gap-1 ${
          stale.level === "stale" ? "text-[#7A2E18] font-medium" :
          stale.level === "warn"  ? "text-[#7A5A11] font-medium" : "text-[#8A8780]"
        }`}>
          <Clock size={9}/>
          <span data-testid={`lead-delay-${lead.id}`}>
            Status: <b className="capitalize">{status}</b> · Delay {fmtDelay(stale.hrs)}
          </span>
        </div>
      </div>

      {canEdit && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {STATUSES.filter(x => x !== status).map(nx => (
            <button key={nx} onClick={() => onMove(lead.id, nx)}
                    className="text-[10px] px-2 py-0.5 border border-[#E5E2D9] rounded hover:bg-[#2D5A27] hover:text-white hover:border-[#2D5A27]">
              → {nx}
            </button>
          ))}
          <button onClick={() => onDelete(lead.id)}
                  className="text-[10px] px-2 py-0.5 text-[#C25D38] ml-auto">
            <Trash size={10}/>
          </button>
        </div>
      )}
    </div>
  );
}
